import { injectable } from 'inversify';

import { OperationResult, Success } from '@vp/common/core/OperationResult';
import { LoadProfileDto } from '@vp/profile/core/dto/LoadProfileDto';
import { ProfileAccessStorage } from '@vp/profile/core/interface/ProfileAccessStorage';
import { ProfileRepository } from '@vp/profile/core/interface/ProfileRepository';
import { ProfileState } from '@vp/profile/core/interface/ProfileState';
import { ProfileModel } from '@vp/profile/core/model/ProfileModel';

@injectable()
export class LoadProfileUsecase {
  constructor(
    private readonly profileRepository: ProfileRepository,
    private readonly profileAccessStorage: ProfileAccessStorage,
    private readonly profileState: ProfileState,
  ) {}

  async execute(dto: LoadProfileDto): Promise<OperationResult<ProfileModel>> {
    this.profileState.loading();
    const resolvedDto = await this.resolveCode(dto);
    return resolvedDto.code ? this.tryUnlock(resolvedDto) : this.tryLoad(resolvedDto);
  }

  private async resolveCode(dto: LoadProfileDto): Promise<LoadProfileDto> {
    if (dto.code) return dto;
    const code = await this.profileAccessStorage.getCode(dto.id);
    return { ...dto, code };
  }

  private async tryUnlock(dto: LoadProfileDto): Promise<OperationResult<ProfileModel>> {
    const result = await this.profileRepository.unlockProfile(dto.id, dto.code!, dto.controller);

    if (result instanceof Success) {
      await this.profileAccessStorage.saveAccess(dto.id, dto.code!);
      this.profileState.success(result.value);
      return result;
    }

    return this.tryLoad(dto);
  }

  private async tryLoad(dto: LoadProfileDto): Promise<OperationResult<ProfileModel>> {
    const result = await this.profileRepository.loadProfile(dto.id, dto.controller);

    if (result instanceof Success) {
      this.profileState.success(result.value);
    } else {
      this.profileState.error();
    }

    return result;
  }
}
